import type { Asset } from "~/modules/assets/types";

interface DealRoomDocument {
    name: string;
    url: string;
}

interface DealRoomProductSummarySection {
    title: string;
    text: string;
}

interface DealRoomConfig {
    blurb: string;
    previewBlurb?: string;
    fullScreenBlurb?: string;
    productSummarySections?: DealRoomProductSummarySection[];
    documents: DealRoomDocument[];
}

interface DealRoomConfigWithAliases extends DealRoomConfig {
    aliases: string[];
}

const DEAL_ROOM_CONFIGS: DealRoomConfigWithAliases[] = [
    {
        aliases: [
            "ACRED",
            "ACREDIT",
            "Apollo Diversified Credit Fund Securitize Feeder",
        ],
        blurb: "ACRED is a Securitize tokenized feeder into Apollo Diversified Credit Fund, a diversified private-credit interval fund with roughly $1.2B of underlying assets and about 63% corporate direct lending exposure. Investors hold feeder shares rather than direct portfolio assets, with a $50k minimum and no stated target return in the reviewed materials. The return source is Apollo's private-credit portfolio, while the wrapper adds feeder-level costs on top of the underlying fund expense structure. Liquidity is quarterly and gated, with 5-25% limits that may be pro-rated, and the feeder wrapper is new as of December 2024. Missing information includes an all-in net yield target for the tokenized feeder, realized feeder performance, and stress-period redemption behavior.",
        previewBlurb:
            "Tokenized feeder aiming for private-credit income through corporate direct lending and Apollo interval-fund exposure.",
        fullScreenBlurb:
            "ACRED is a Securitize tokenized feeder into Apollo Diversified Credit Fund, a diversified private-credit interval fund with roughly $1.2B of underlying assets and about 63% corporate direct lending exposure. Investors hold feeder shares rather than direct portfolio assets, with a $50k minimum and no stated target return in the reviewed materials. The return source is Apollo's private-credit portfolio, while the wrapper adds feeder-level costs on top of the underlying fund expense structure. Liquidity is quarterly and gated, with 5-25% limits that may be pro-rated, and the feeder wrapper is new as of December 2024. Missing information includes an all-in net yield target for the tokenized feeder, realized feeder performance, and stress-period redemption behavior.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target return disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Feeder exposure to Apollo Diversified Credit Fund, a diversified private-credit interval fund with a large corporate direct lending allocation.",
            },
            {
                title: "Liquidity",
                text: "Liquidity comes through quarterly repurchase offers, typically capped at 5% of the underlying fund's shares and potentially up to 25% at the board's discretion. Requests can be prorated, delayed or resubmitted if the offer is oversubscribed.",
            },
        ],
        documents: [
            {
                name: "ACRED PPM",
                url: "/data-rooms/acred/ACRED---PPM.pdf",
            },
            {
                name: "ACRED Presentation",
                url: "/data-rooms/acred/ACRED---Presentation.pdf",
            },
        ],
    },
    {
        aliases: [
            "SECURITIZE-AAA-CLO",
            "AAA-CLO",
            "Securitize AAA CLO Tokenized Fund",
        ],
        blurb: "Securitize AAA CLO Tokenized Fund is a BVI tokenized fund offering shares intended to invest primarily in senior AAA-rated CLO tranches. The instrument is fund equity rather than a direct note, with a $100k minimum and no disclosed current AUM or portfolio schedule in the reviewed PPM. The return source is expected CLO tranche income, but the materials do not state a target yield, realized return, spread, or current holdings. Liquidity depends on the fund's ability to sell CLO assets, and the vehicle is newly formed in October 2025. Missing information includes portfolio holdings, manager allocation, net yield, available capacity, rating composition, and redemption execution history.",
        previewBlurb:
            "Tokenized fund aiming for CLO income through senior AAA-rated CLO tranches; target yield is not disclosed.",
        fullScreenBlurb:
            "Securitize AAA CLO Tokenized Fund is a BVI tokenized fund offering shares intended to invest primarily in senior AAA-rated CLO tranches. The instrument is fund equity rather than a direct note, with a $100k minimum and no disclosed current AUM or portfolio schedule in the reviewed PPM. The return source is expected CLO tranche income, but the materials do not state a target yield, realized return, spread, or current holdings. Liquidity depends on the fund's ability to sell CLO assets, and the vehicle is newly formed in October 2025. Missing information includes portfolio holdings, manager allocation, net yield, available capacity, rating composition, and redemption execution history.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target yield disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Primarily senior AAA-rated CLO tranches held through a BVI tokenized fund share structure.",
            },
            {
                title: "Liquidity",
                text: "Withdrawals can be requested on business days after a 24-hour lock-up. Cash is generally paid after the fund sells CLO holdings, expected around T+2/T+3, plus up to two business days after proceeds are received; liquidity-pool redemptions may be limited and can carry a 2% fee.",
            },
        ],
        documents: [
            {
                name: "Securitize AAA CLO Tokenized Fund PPM",
                url: "/data-rooms/securitize-aaa-clo/Securitize-AAA-CLO-Tokenized-Fund---PPM-(clean).pdf",
            },
        ],
    },
    {
        aliases: ["CUSHY", "Coinbase Stablecoin Yield Fund"],
        blurb: "Coinbase Stablecoin Yield Fund, or CUSHY, is a feeder-fund interest targeting opportunistic USDC credit across BTC lending, web3 credit, and tokenized CLO, RMBS and asset-backed exposures. The materials describe a $1M minimum, target capacity above $2B, and a gross return target of SOFR plus 400-700 bps. The return is compensation for lending and structured-credit risk in digital-asset markets, including liquidity, collateral, counterparty and protocol risks. The strategy is pre-launch, permits up to 20% undercollateralized exposure, and may use looping leverage that can amplify liquidation cascades. Missing information includes realized performance, seeded portfolio composition, final net yield after fees, and live drawdown history.",
        previewBlurb:
            "USDC credit feeder aiming for SOFR + 400-700 bps through crypto lending, web3 credit and tokenized structured credit.",
        fullScreenBlurb:
            "Coinbase Stablecoin Yield Fund, or CUSHY, is a feeder-fund interest targeting opportunistic USDC credit across BTC lending, web3 credit, and tokenized CLO, RMBS and asset-backed exposures. The materials describe a $1M minimum, target capacity above $2B, and a gross return target of SOFR plus 400-700 bps. The return is compensation for lending and structured-credit risk in digital-asset markets, including liquidity, collateral, counterparty and protocol risks. The strategy is pre-launch, permits up to 20% undercollateralized exposure, and may use looping leverage that can amplify liquidation cascades. Missing information includes realized performance, seeded portfolio composition, final net yield after fees, and live drawdown history.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "SOFR + 400-700 bps net return for Share Class A/B.",
            },
            {
                title: "Investment Universe",
                text: "Opportunistic USDC credit across BTC lending, web3 credit, tokenized CLOs, RMBS and other asset-backed exposures.",
            },
            {
                title: "Liquidity",
                text: "Subscriptions are monthly and redemptions are quarterly with quarterly notice. The materials list no gate, but redemptions may be delayed if underlying credit or crypto markets are stressed.",
            },
        ],
        documents: [
            {
                name: "Coinbase Stablecoin Yield Fund Materials",
                url: "/data-rooms/cushy/Coinbase-Stablecoin-Yield-Fund-(CUSHY)(4.16.26).pdf",
            },
        ],
    },
    {
        aliases: [
            "USCC",
            "Bitwise Crypto Carry Fund",
            "bitwise-crypto-carry-fund",
        ],
        blurb: "Bitwise Crypto Carry Fund, or USCC, offers qualified purchasers exposure to crypto basis strategies that seek to capture the difference between spot and futures prices across crypto assets and U.S. Treasury securities. Subscriptions and redemptions are facilitated through USD or USDC, with liquidity each market day.",
        previewBlurb:
            "Crypto carry fund aiming to capture basis between spot and futures markets across crypto assets and tokenized U.S. Treasury Funds.",
        fullScreenBlurb:
            "Bitwise Crypto Carry Fund, or USCC, offers qualified purchasers exposure to crypto basis strategies that seek to capture the difference between spot and futures prices across crypto assets and U.S. Treasury securities. Subscriptions and redemptions are facilitated through USD or USDC, with liquidity each market day.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target return disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Crypto basis strategies across crypto assets and U.S. Treasury securities, with subscriptions and redemptions in USD or USDC.",
            },
            {
                title: "Liquidity",
                text: "Liquidity is available each market day. Redemption requests before 5pm ET use that day's NAV and pay on T+1; later requests use the next market day's NAV and generally pay on T+2, with USDC redemptions dependent on Circle and fund-document restrictions.",
            },
        ],
        documents: [
            {
                name: "USCC Product Materials",
                url: "https://superstate.co/uscc",
            },
        ],
    },
    {
        aliases: ["PRIME", "prime", "Democratized Prime"],
        blurb: "PRIME is a tokenized deposit into Democratized Prime, a warehouse lending facility for HELOCs originated by Figure Technologies. Token holders earn yield by lending against performing HELOCs awaiting securitization, with loans removed from the collateral pool once they are 60+ days delinquent.",
        previewBlurb:
            "Warehouse lending exposure backed by performing HELOCs originated by Figure Technologies.",
        fullScreenBlurb:
            "PRIME is a tokenized deposit into Democratized Prime, a warehouse lending facility for HELOCs originated by Figure Technologies. Token holders earn yield by lending against performing HELOCs awaiting securitization, with loans removed from the collateral pool once they are 60+ days delinquent.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "Up to 8.5% variable yield.",
            },
            {
                title: "Investment Universe",
                text: "Performing HELOCs originated by Figure Technologies and held in a warehouse facility before securitization.",
            },
            {
                title: "Liquidity",
                text: "PRIME can trade on DEXs, but a secondary market is not guaranteed and users may be unable to sell at their desired price or at all. Unstaking can be delayed, locked or otherwise restricted; the public terms do not state a maximum delay.",
            },
        ],
        documents: [
            {
                name: "PRIME Product Materials",
                url: "https://hastra.io/prime",
            },
        ],
    },
    {
        aliases: ["GROMA-NAV-REIT", "Groma NAV REIT"],
        blurb: "Groma NAV REIT is common equity in a Maryland corporation investing in small and mid-sized multifamily properties, initially focused on Greater Boston. The room states $58.7M raised, a $1k minimum, and a 2.50% annualized distribution reported by the manager. The return source is rental income, renovation upside and manager-marked property NAV rather than contractual credit repayment. The position is equity junior to mortgage debt, and the company states it has not yet qualified as a REIT. Missing information includes property-level NOI, occupancy, debt yield, independent valuation support, and audited fund-level performance.",
        previewBlurb:
            "Common equity aiming for 2.50% annualized distributions through small and mid-sized Greater Boston multifamily rentals.",
        fullScreenBlurb:
            "Groma NAV REIT is common equity in a Maryland corporation investing in small and mid-sized multifamily properties, initially focused on Greater Boston. The room states $58.7M raised, a $1k minimum, and a 2.50% annualized distribution reported by the manager. The return source is rental income, renovation upside and manager-marked property NAV rather than contractual credit repayment. The position is equity junior to mortgage debt, and the company states it has not yet qualified as a REIT. Missing information includes property-level NOI, occupancy, debt yield, independent valuation support, and audited fund-level performance.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "6.0-7.25% target yield.",
            },
            {
                title: "Investment Universe",
                text: "Common equity in small and mid-sized multifamily properties, initially focused on Greater Boston.",
            },
            {
                title: "Liquidity",
                text: "The issuer may offer quarterly redemptions, but the program is discretionary and capped. Requests can be reduced, suspended or left unfilled if available cash or program limits are insufficient.",
            },
        ],
        documents: [
            {
                name: "Groma NAV REIT PPM",
                url: "/data-rooms/groma-nav-reit/Groma-NAV-REIT-PPM-v1.30-(Q4-2025).pdf",
            },
        ],
    },
    {
        aliases: ["VISION-87", "CHAMPFLEURY", "Vision 87 Champfleury"],
        blurb: "Vision 87 Champfleury is Class A participating equity in a ground-up 87-unit multifamily development in Laval, Quebec. The PPM describes a raise up to $23M, a $10k minimum, and a 14.1% projected IRR based on BDO-supported projections. The return source is development profit and rental value creation, not contractual credit cash flow. Investors are exposed to single-asset construction, leasing, financing, cost-overrun and FX risk, with distributions dependent on project outcomes and issuer discretion. Missing information includes debt-yield metrics, final capital stack, hedging policy, executed GMP support, and independent stabilized NOI evidence.",
        previewBlurb:
            "Participating equity aiming for 14.1% projected IRR through ground-up multifamily development in Laval, Quebec.",
        fullScreenBlurb:
            "Vision 87 Champfleury is Class A participating equity in a ground-up 87-unit multifamily development in Laval, Quebec. The PPM describes a raise up to $23M, a $10k minimum, and a 14.1% projected IRR based on BDO-supported projections. The return source is development profit and rental value creation, not contractual credit cash flow. Investors are exposed to single-asset construction, leasing, financing, cost-overrun and FX risk, with distributions dependent on project outcomes and issuer discretion. Missing information includes debt-yield metrics, final capital stack, hedging policy, executed GMP support, and independent stabilized NOI evidence.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "14.1% target IRR and 27.3% 15-month ROI.",
            },
            {
                title: "Investment Universe",
                text: "Class A participating equity in a ground-up 87-unit multifamily development in Laval, Quebec.",
            },
            {
                title: "Liquidity",
                text: "Exits are expected mainly from a future sale or refinancing of the property. Secondary trading may be possible after one year, but availability is limited and there is no guaranteed buyer.",
            },
        ],
        documents: [
            {
                name: "Vision 87 Champfleury PPM",
                url: "/data-rooms/vision-87-champfleury/Private-Placement-Memorandum-(PPM)_Vision-87-Champfleury-(Final)-(1).pdf",
            },
        ],
    },
    {
        aliases: ["DOMEBRIDGE-AZ", "DOMEBRIDGE", "DomeBridge AZ"],
        blurb: "DomeBridge AZ offers profit-participation tokens linked to a Wyoming LLC that owns mineral interests in a development-stage Arizona copper-gold deposit. The PPM describes a $150M offering, a $50k minimum, and sponsor-projected 1.4x-35x re-rating potential rather than current yield. The return source is commodity-resource development and valuation uplift, not operating cash flow or contractual debt service. The asset is pre-feasibility with inferred resources and an $11B sponsor NAV estimate, so recovery depends on permitting, financing, development execution and commodity prices. Missing information includes bankable feasibility, reserve conversion, definitive mine plan, audited sponsor valuation support, and clear downside recovery analysis.",
        previewBlurb:
            "Profit-participation token aiming for resource re-rating upside through Arizona copper-gold mineral interests.",
        fullScreenBlurb:
            "DomeBridge AZ offers profit-participation tokens linked to a Wyoming LLC that owns mineral interests in a development-stage Arizona copper-gold deposit. The PPM describes a $150M offering, a $50k minimum, and sponsor-projected 1.4x-35x re-rating potential rather than current yield. The return source is commodity-resource development and valuation uplift, not operating cash flow or contractual debt service. The asset is pre-feasibility with inferred resources and an $11B sponsor NAV estimate, so recovery depends on permitting, financing, development execution and commodity prices. Missing information includes bankable feasibility, reserve conversion, definitive mine plan, audited sponsor valuation support, and clear downside recovery analysis.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "1.4x-35.0x sponsor-projected re-rating potential.",
            },
            {
                title: "Investment Universe",
                text: "Profit-participation tokens linked to development-stage Arizona copper-gold mineral interests.",
            },
            {
                title: "Liquidity",
                text: "Tokens are locked for three years. After that, investors may seek secondary trading on supported regulated venues, but transfer approvals, venue availability and buyer demand are not guaranteed.",
            },
        ],
        documents: [
            {
                name: "DomeBridge AZ PPM",
                url: "/data-rooms/domebridge-az/DOMEBRIDGE_-AZ_-Private_Placement_Memorandum.pdf",
            },
        ],
    },
    {
        aliases: [
            "HLPIF",
            "HAMILTON-LANE-INFRA",
            "Hamilton Lane Private Infrastructure Fund",
        ],
        blurb: "Hamilton Lane Private Infrastructure Fund is a non-traded closed-end Delaware fund investing in infrastructure equity and debt through co-investments, secondaries and related strategies. The prospectus describes an offering up to $1B, a $25k minimum, and a 2-5% distribution target. The return source is underlying private-infrastructure asset performance rather than contractual fund-level repayment. Liquidity is limited to discretionary repurchase offers expected up to 5% of NAV per quarter, and Class R gross fees total 4.49%. Missing information includes operating history, seasoned NAV behavior, realized distribution coverage, and a current portfolio schedule.",
        previewBlurb:
            "Closed-end fund aiming for 2-5% distributions through private infrastructure equity, debt, co-investments and secondaries.",
        fullScreenBlurb:
            "Hamilton Lane Private Infrastructure Fund is a non-traded closed-end Delaware fund investing in infrastructure equity and debt through co-investments, secondaries and related strategies. The prospectus describes an offering up to $1B, a $25k minimum, and a 2-5% distribution target. The return source is underlying private-infrastructure asset performance rather than contractual fund-level repayment. Liquidity is limited to discretionary repurchase offers expected up to 5% of NAV per quarter, and Class R gross fees total 4.49%. Missing information includes operating history, seasoned NAV behavior, realized distribution coverage, and a current portfolio schedule.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "2-5% annualized NAV distribution target.",
            },
            {
                title: "Investment Universe",
                text: "Private infrastructure equity and debt through co-investments, secondaries and related strategies in a non-traded closed-end fund.",
            },
            {
                title: "Liquidity",
                text: "There is no redemption on demand. The fund expects to consider quarterly repurchase offers of up to about 5% of net assets, but offers are discretionary and can be oversubscribed, prorated or suspended.",
            },
        ],
        documents: [
            {
                name: "Hamilton Lane Private Infrastructure Fund Prospectus",
                url: "/data-rooms/hlpif/hamilton-lane-private-infrastructure-fund_prospectus.pdf",
            },
        ],
    },
    {
        aliases: ["APPALOOSA-DENTAL", "PAEAN-I", "Appaloosa Dental"],
        blurb: "Appaloosa Dental Partners/Paean I is a Class A LP membership interest in a dental DSO roll-up fund focused on practice acquisitions. The subscription materials and model describe a $10M fund with capacity up to $20M, a $50k minimum, and a target of at least 2.0x gross MOIC. The return source is acquisition, integration and exit value creation rather than contractual credit repayment. The fund is a first-time blind pool with no realized track record, stacked fees and a lockup of at least six years. Missing information includes executed acquisitions, audited manager performance, debt terms, practice-level diligence, and realized exit evidence.",
        previewBlurb:
            "Class A LP interest aiming for 2.0x+ gross MOIC through dental practice acquisitions, integration and exit value creation.",
        fullScreenBlurb:
            "Appaloosa Dental Partners/Paean I is a Class A LP membership interest in a dental DSO roll-up fund focused on practice acquisitions. The subscription materials and model describe a $10M fund with capacity up to $20M, a $50k minimum, and a target of at least 2.0x gross MOIC. The return source is acquisition, integration and exit value creation rather than contractual credit repayment. The fund is a first-time blind pool with no realized track record, stacked fees and a lockup of at least six years. Missing information includes executed acquisitions, audited manager performance, debt terms, practice-level diligence, and realized exit evidence.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "10% per annum cumulative, non-compounding preferred return.",
            },
            {
                title: "Investment Universe",
                text: "Class A LP interest in a dental DSO roll-up fund focused on practice acquisitions, integration and exit value creation.",
            },
            {
                title: "Liquidity",
                text: "Liquidity is very limited: investors should expect to hold until a fund wind-up or exit event. There is no withdrawal right before then, and transfers require manager consent.",
            },
        ],
        documents: [
            {
                name: "Illustrative Practice Level P&L",
                url: "/data-rooms/appaloosa-dental/Appaloosa-Dental-Partners---Illustrative-Practice-Level-PL.xlsx",
            },
            {
                name: "Paean Management Fund I Side Letter",
                url: "/data-rooms/appaloosa-dental/Paean-Management-Fund-I-LLC-Side-Letter.pdf",
            },
            {
                name: "Paean Management Fund I Subscription Package",
                url: "/data-rooms/appaloosa-dental/Paean-Management-Fund-I-LLC-Subscription-Package.pdf",
            },
        ],
    },
    {
        aliases: [
            "SPIKO-CASH-CARRY",
            "SPIKO-C-C",
            "Spiko Digital Assets Cash and Carry Fund",
        ],
        blurb: "Spiko Digital Assets Cash and Carry Fund is a tokenized French professional specialized fund investing in crypto basis trades through structured notes and certificates. The prospectus describes a 100k USD or EUR minimum and does not state a target yield, current AUM or complete fee economics in the reviewed materials. The return source is basis capture between spot and derivative crypto markets rather than contractual borrower repayment. Investors take capital-at-risk exposure to counterparty, structured-note, market, DLT and operational risks. Missing information includes expected net return, live exposure, counterparty schedule, fee load, leverage, and stress-period basis performance.",
        previewBlurb:
            "Tokenized specialized fund aiming for basis-trade returns through crypto spot, derivative and structured-note exposure.",
        fullScreenBlurb:
            "Spiko Digital Assets Cash and Carry Fund is a tokenized French professional specialized fund investing in crypto basis trades through structured notes and certificates. The prospectus describes a 100k USD or EUR minimum and does not state a target yield, current AUM or complete fee economics in the reviewed materials. The return source is basis capture between spot and derivative crypto markets rather than contractual borrower repayment. Investors take capital-at-risk exposure to counterparty, structured-note, market, DLT and operational risks. Missing information includes expected net return, live exposure, counterparty schedule, fee load, leverage, and stress-period basis performance.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target return disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Crypto basis trades through structured notes and certificates inside a tokenized French professional specialized fund.",
            },
            {
                title: "Liquidity",
                text: "Shares are designed for daily dealing with daily order processing and payment after NAV calculation. Redemptions can still be gated, suspended or delayed if markets, counterparties or fund operations are stressed.",
            },
        ],
        documents: [
            {
                name: "Spiko Cash & Carry Prospectus",
                url: "/data-rooms/spiko-cash-carry/Prospectus-Spiko-CC.pdf",
            },
        ],
    },
    {
        aliases: ["POLYTRADE", "Polytrade eNote"],
        blurb: "Polytrade is a senior-secured USDC eNote funding SME trade receivables and invoice factoring exposure. The factsheet describes size up to USDC 1M, a USDC 1k minimum, and a fixed 10.50% per annum coupon. The return source is invoice discount economics supported by insurance and receivable collections. The position is exposed to a single thinly capitalized originator, insurer performance and documentation timing, and the factsheet contains a tenor discrepancy. Missing information includes audited originator financials, insurer policy detail, loan-level receivable tape, historical losses, and a reconciled maturity schedule.",
        previewBlurb:
            "Senior-secured USDC eNote aiming for a 10.50% coupon through SME trade receivables and invoice factoring.",
        fullScreenBlurb:
            "Polytrade is a senior-secured USDC eNote funding SME trade receivables and invoice factoring exposure. The factsheet describes size up to USDC 1M, a USDC 1k minimum, and a fixed 10.50% per annum coupon. The return source is invoice discount economics supported by insurance and receivable collections. The position is exposed to a single thinly capitalized originator, insurer performance and documentation timing, and the factsheet contains a tenor discrepancy. Missing information includes audited originator financials, insurer policy detail, loan-level receivable tape, historical losses, and a reconciled maturity schedule.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "10.50% p.a. coupon.",
            },
            {
                title: "Investment Universe",
                text: "Senior-secured USDC eNote funding SME trade receivables and invoice factoring exposure.",
            },
            {
                title: "Liquidity",
                text: "Liquidity is mainly through repayment at the short two-month maturity. The factsheet does not describe early redemption or secondary-market liquidity, so investors should expect to hold to maturity.",
            },
        ],
        documents: [
            {
                name: "Polytrade Factsheet",
                url: "/data-rooms/polytrade/Factsheet-Polytrade-Jan-2024.pdf",
            },
        ],
    },
    {
        aliases: ["KIMURA", "Kimura Commodity Trade Finance"],
        blurb: "Kimura is a senior unsecured eNote funding a single commodity transit-finance transaction managed by Kimura Commodity Trade Finance Fund Limited. The factsheet describes a USD 10M size, USDC 10k minimum, and 8.7% per annum contractual coupon. The return source is commodity trade-finance spread on one transaction, supported by stated 114% overcollateralization. The note remains unsecured despite the overcollateralization language and carries single-obligor concentration with the obligor unnamed. Missing information includes obligor identity, audited credit profile, collateral control, legal security package, payment waterfall and transaction loss history.",
        previewBlurb:
            "Senior unsecured eNote aiming for an 8.7% coupon through a single commodity transit-finance transaction.",
        fullScreenBlurb:
            "Kimura is a senior unsecured eNote funding a single commodity transit-finance transaction managed by Kimura Commodity Trade Finance Fund Limited. The factsheet describes a USD 10M size, USDC 10k minimum, and 8.7% per annum contractual coupon. The return source is commodity trade-finance spread on one transaction, supported by stated 114% overcollateralization. The note remains unsecured despite the overcollateralization language and carries single-obligor concentration with the obligor unnamed. Missing information includes obligor identity, audited credit profile, collateral control, legal security package, payment waterfall and transaction loss history.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "8.7% p.a. coupon.",
            },
            {
                title: "Investment Universe",
                text: "Senior unsecured eNote funding a single commodity transit-finance transaction managed by Kimura Commodity Trade Finance Fund Limited.",
            },
            {
                title: "Liquidity",
                text: "Liquidity is mainly through repayment at the three-month maturity. The factsheet does not describe early redemption or secondary-market liquidity, so exits depend on scheduled repayment.",
            },
        ],
        documents: [
            {
                name: "Kimura Factsheet",
                url: "/data-rooms/kimura/Factsheet-Kimura-Jan-2024.pdf",
            },
        ],
    },
    {
        aliases: ["TRADEFLOW", "TRADE-FLOW", "TradeFlow"],
        blurb: "TradeFlow is a subordinated unsecured eNote funding physical commodity import and export flows for SME trade finance. The factsheet describes a USD 10M note within an approximately USD 100M fund, a USDC 10k minimum, and an 8% per annum coupon. The return source is commodity trade-finance spread from pre-booked physical flows and related working-capital financing. The note ranks below a $9M senior bond and relies on insurance payout and commodity liquidation for downside recovery. Missing information includes obligor identities, collateral-control mechanics, insurer policy terms, senior-bond covenants, and realized loss history.",
        previewBlurb:
            "Subordinated unsecured eNote aiming for an 8% coupon through physical commodity import/export trade finance.",
        fullScreenBlurb:
            "TradeFlow is a subordinated unsecured eNote funding physical commodity import and export flows for SME trade finance. The factsheet describes a USD 10M note within an approximately USD 100M fund, a USDC 10k minimum, and an 8% per annum coupon. The return source is commodity trade-finance spread from pre-booked physical flows and related working-capital financing. The note ranks below a $9M senior bond and relies on insurance payout and commodity liquidation for downside recovery. Missing information includes obligor identities, collateral-control mechanics, insurer policy terms, senior-bond covenants, and realized loss history.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "8% p.a. coupon.",
            },
            {
                title: "Investment Universe",
                text: "Subordinated unsecured eNote funding physical commodity import and export flows for SME trade finance.",
            },
            {
                title: "Liquidity",
                text: "The factsheet states 90-day liquidity, with coupon payment expected at maturity. It does not describe an early redemption process, so exits should be viewed around the stated liquidity window.",
            },
        ],
        documents: [
            {
                name: "TradeFlow Factsheet",
                url: "/data-rooms/tradeflow/Factsheet-Tradeflow-Feb-2024.pdf",
            },
        ],
    },
    {
        aliases: [
            "ORIGINATE-JUNIOR",
            "ORIGINATE-CAPITAL",
            "Originate Capital Junior",
        ],
        blurb: "Originate Capital Junior is a junior first-loss eNote referencing US trade-finance receivables originated by Originate Capital. The factsheet describes a USD 2M tranche, USDC 10k minimum, and a 16-18% gross target return. The return source is first-loss compensation on invoice discounting and receivable finance exposure. The collateral book is only about USD 2.5M, so a single debtor default could materially impair or wipe the tranche. Missing information includes debtor concentration, senior-tranche terms, audited originator financials, historical defaults, recovery history and net yield after fees.",
        previewBlurb:
            "Junior first-loss eNote aiming for a 16-18% gross return through US trade-finance receivables exposure.",
        fullScreenBlurb:
            "Originate Capital Junior is a junior first-loss eNote referencing US trade-finance receivables originated by Originate Capital. The factsheet describes a USD 2M tranche, USDC 10k minimum, and a 16-18% gross target return. The return source is first-loss compensation on invoice discounting and receivable finance exposure. The collateral book is only about USD 2.5M, so a single debtor default could materially impair or wipe the tranche. Missing information includes debtor concentration, senior-tranche terms, audited originator financials, historical defaults, recovery history and net yield after fees.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "16-18% p.a. target range.",
            },
            {
                title: "Investment Universe",
                text: "Junior first-loss eNote referencing U.S. trade-finance receivables originated by Originate Capital.",
            },
            {
                title: "Liquidity",
                text: "Liquidity is mainly at the 12-month maturity, with quarterly coupon payments during the term. The factsheet does not describe early redemption or secondary-market liquidity.",
            },
        ],
        documents: [
            {
                name: "Originate Capital Junior Tranche Factsheet",
                url: "/data-rooms/originate-junior/Factsheet-Originate-Capital---Jan-2024-Junior-Tranche.pdf",
            },
        ],
    },
    {
        aliases: ["ARIA-BRAZIL", "Aria Commodities Brazil"],
        blurb: "Aria Commodities Brazil is a senior-secured eNote financing food-commodity trade flow from Brazil to a Chinese state-owned enterprise counterparty. The factsheet describes a USD 5M size, USD 10k minimum, and an indicated 12-13.8% return. The return source is short-dated commodity trade-finance spread, supported by trade-flow collateral and transaction structure. The exposure is concentrated in one issuer, one commodity-flow program and a China SOE counterparty. Missing information includes vehicle loss history, audited issuer financials, collateral-control detail, buyer credit support, final coupon and insurance terms.",
        previewBlurb:
            "Senior-secured eNote aiming for a 12-13.8% return through Brazil-to-China food-commodity trade flows.",
        fullScreenBlurb:
            "Aria Commodities Brazil is a senior-secured eNote financing food-commodity trade flow from Brazil to a Chinese state-owned enterprise counterparty. The factsheet describes a USD 5M size, USD 10k minimum, and an indicated 12-13.8% return. The return source is short-dated commodity trade-finance spread, supported by trade-flow collateral and transaction structure. The exposure is concentrated in one issuer, one commodity-flow program and a China SOE counterparty. Missing information includes vehicle loss history, audited issuer financials, collateral-control detail, buyer credit support, final coupon and insurance terms.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "12-13.8% p.a. indicated return.",
            },
            {
                title: "Investment Universe",
                text: "Senior-secured eNote financing food-commodity trade flow from Brazil to a Chinese state-owned enterprise counterparty.",
            },
            {
                title: "Liquidity",
                text: "Underlying trades are short dated at about 30-40 days, but the investor note has a 12-month term. Liquidity therefore depends on scheduled note repayment rather than on-demand withdrawal.",
            },
        ],
        documents: [
            {
                name: "Aria Commodities Brazil Factsheet",
                url: "/data-rooms/aria-brazil/Brazil---Factsheet-Aria-commodities-Feb-2024.pdf",
            },
        ],
    },
    {
        aliases: ["ARIA-SRI-LANKA", "Aria Commodities Sri Lanka"],
        blurb: "Aria Commodities Sri Lanka is a senior-secured asset-backed eNote financing fuel-import trade finance into Sri Lanka. The factsheet describes a USD 60M size, USD 10k minimum, and an indicated 12-13.8% return. The return source is short-dated import trade-finance spread, partly mitigated by a Dubai-bank letter of credit and Lloyds of London syndicate insurance disclosure. The main credit risk is Sri Lanka sovereign and country exposure combined with issuer and transaction concentration. Missing information includes borrower financials, LC terms, insurance exclusions, realized loss history, final coupon and collateral liquidation mechanics.",
        previewBlurb:
            "Senior-secured asset-backed eNote aiming for a 12-13.8% return through Sri Lanka fuel-import trade finance.",
        fullScreenBlurb:
            "Aria Commodities Sri Lanka is a senior-secured asset-backed eNote financing fuel-import trade finance into Sri Lanka. The factsheet describes a USD 60M size, USD 10k minimum, and an indicated 12-13.8% return. The return source is short-dated import trade-finance spread, partly mitigated by a Dubai-bank letter of credit and Lloyds of London syndicate insurance disclosure. The main credit risk is Sri Lanka sovereign and country exposure combined with issuer and transaction concentration. Missing information includes borrower financials, LC terms, insurance exclusions, realized loss history, final coupon and collateral liquidation mechanics.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "12-13.8% p.a. indicated return.",
            },
            {
                title: "Investment Universe",
                text: "Senior-secured asset-backed eNote financing fuel-import trade finance into Sri Lanka.",
            },
            {
                title: "Liquidity",
                text: "Underlying trades roll monthly at about 30-35 days, while the note has a 12-month tenor with an option to roll. Exits should be viewed as term-based rather than on demand.",
            },
        ],
        documents: [
            {
                name: "Aria Commodities Sri Lanka Factsheet",
                url: "/data-rooms/aria-sri-lanka/Sri-Lanka---Factsheet-Aria-commodities-Feb-2024.pdf",
            },
        ],
    },
    {
        aliases: [
            "HYBOND",
            "Tokenize Global Finance HYBOND",
            "BNY Mellon Global High Yield",
            "BNY Mellon Global Funds HYBOND",
        ],
        blurb: "HYBOND is a Tokenize Global Finance factsheet linked to BNY Mellon Global Funds plc exposure in the Bond Global High Yield USD sector. The document identifies an Irish UCITS fund launched in November 2016, managed by Insight, with USD base currency, multiple currency share classes, and SOFR 90-day compounded as the cash benchmark. The return source is global high-yield bond exposure managed to deliver positive returns above the cash benchmark over a rolling three-year basis, though positive return is not guaranteed. The reviewed sheet provides fund-level performance and benchmark history but does not define the token wrapper's legal rights, fees, redemption mechanics or holder recourse. Missing information includes the token terms, current token AUM, all-in net yield after wrapper fees, custody and proof-of-reserve mechanics, and stress-period redemption behavior.",
        previewBlurb:
            "Tokenized fund exposure aiming to beat SOFR over rolling three-year periods through global high-yield bonds.",
        fullScreenBlurb:
            "HYBOND is a Tokenize Global Finance factsheet linked to BNY Mellon Global Funds plc exposure in the Bond Global High Yield USD sector. The document identifies an Irish UCITS fund launched in November 2016, managed by Insight, with USD base currency, multiple currency share classes, and SOFR 90-day compounded as the cash benchmark. The return source is global high-yield bond exposure managed to deliver positive returns above the cash benchmark over a rolling three-year basis, though positive return is not guaranteed. The reviewed sheet provides fund-level performance and benchmark history but does not define the token wrapper's legal rights, fees, redemption mechanics or holder recourse. Missing information includes the token terms, current token AUM, all-in net yield after wrapper fees, custody and proof-of-reserve mechanics, and stress-period redemption behavior.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "Positive returns greater than SOFR 90-day compounded on a 3-year rolling basis.",
            },
            {
                title: "Investment Universe",
                text: "Tokenized exposure linked to BNY Mellon Global Funds plc in the Bond Global High Yield USD sector.",
            },
            {
                title: "Liquidity",
                text: "The referenced BNY Mellon fund offers business-day dealing during stated hours and lists no entry or exit charge. The token wrapper, however, does not specify guaranteed redemption timing or token-holder recourse.",
            },
        ],
        documents: [
            {
                name: "HYBOND One-Pager Factsheet",
                url: "/data-rooms/hybond/HYBOND---One-Pager-Factsheet.pdf",
            },
        ],
    },
    {
        aliases: [
            "MF-ONE",
            "MFONE",
            "mF-ONE",
            "Midas mF-ONE",
            "Midas F-ONE",
            "Fasanara F-ONE",
        ],
        blurb: "Midas mF-ONE is a tokenized bearer bond certificate issued by Midas Software GmbH in Germany for non-US qualified investors seeking exposure to Fasanara Capital's diversified credit strategy. The factsheet states no minimum investment, $70M AUM, an 11.4% seven-day APY, and an estimated 9-12% blended target yield. The return source is a tiered stack of about 10% mTBILL for instant liquidity, variable Fasanara Digital Pareto Vault BTC-basis exposure for seven-day liquidity, and up to 90% Fasanara F-ONE private credit exposure spanning receivables, consumer loans and liquid strategies. Liquidity depends on the tier accessed, with instant, seven-day and quarterly redemption paths plus token-level subscription and redemption fees. Missing information includes final note terms, large-scale liquidation depth, underlying F-ONE portfolio tape, complete fee drag, and stress-period redemption history.",
        previewBlurb:
            "Bearer bond certificate aiming for 9-12% blended yield through Fasanara private credit, mTBILLs and BTC basis.",
        fullScreenBlurb:
            "Midas mF-ONE is a tokenized bearer bond certificate issued by Midas Software GmbH in Germany for non-US qualified investors seeking exposure to Fasanara Capital's diversified credit strategy. The factsheet states no minimum investment, $70M AUM, an 11.4% seven-day APY, and an estimated 9-12% blended target yield. The return source is a tiered stack of about 10% mTBILL for instant liquidity, variable Fasanara Digital Pareto Vault BTC-basis exposure for seven-day liquidity, and up to 90% Fasanara F-ONE private credit exposure spanning receivables, consumer loans and liquid strategies. Liquidity depends on the tier accessed, with instant, seven-day and quarterly redemption paths plus token-level subscription and redemption fees. Missing information includes final note terms, large-scale liquidation depth, underlying F-ONE portfolio tape, complete fee drag, and stress-period redemption history.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "Estimated 9-12% blended yield.",
            },
            {
                title: "Investment Universe",
                text: "Fasanara private credit exposure, mTBILL liquidity allocation and variable BTC-basis exposure through a tokenized bearer bond certificate.",
            },
            {
                title: "Liquidity",
                text: "Liquidity is tiered: about 10% is intended for instant mTBILL liquidity, a BTC-basis sleeve targets seven-day liquidity, and the larger F-ONE private-credit sleeve is quarterly. Faster token redemptions can carry subscription or redemption fees.",
            },
        ],
        documents: [
            {
                name: "Midas mF-ONE Factsheet",
                url: "/data-rooms/mf-one/Midas---mf-ONE---Factsheet.pdf",
            },
        ],
    },
    {
        aliases: [
            "mGLOBAL",
            "MGLOBAL",
            "Midas Fasanara Global",
            "midas-fasanara-global",
        ],
        blurb: "Midas Fasanara Global, or mGLOBAL, provides onchain exposure to Fasanara's Global Fund strategy, which invests primarily in trade receivables and digital invoices bought from SMEs. The underlying receivables are short-dated and owed by diversified corporate obligors, with Fasanara acting as investment manager.",
        previewBlurb:
            "Onchain exposure to Fasanara's Global Fund strategy through short-dated trade receivables and digital invoices.",
        fullScreenBlurb:
            "Midas Fasanara Global, or mGLOBAL, provides onchain exposure to Fasanara's Global Fund strategy, which invests primarily in trade receivables and digital invoices bought from SMEs. The underlying receivables are short-dated and owed by diversified corporate obligors, with Fasanara acting as investment manager.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target return disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Short-dated trade receivables and digital invoices bought from SMEs and owed by diversified corporate obligors.",
            },
            {
                title: "Liquidity",
                text: "Instant redemptions are available only up to a capped liquidity pool. Larger or standard redemptions are processed after price updates and can take longer if the pool is depleted or underlying receivable cash flows need to be realized.",
            },
        ],
        documents: [
            {
                name: "mGLOBAL Product Materials",
                url: "https://midas.app/mglobal",
            },
        ],
    },
    {
        aliases: [
            "CRDYX",
            "CRDT",
            "WisdomTree Private Credit and Alternative Income Digital Fund",
            "wisdomtree-private-credit-and-alternative-income-digital-fund",
        ],
        blurb: "WisdomTree Private Credit and Alternative Income Digital Fund, or CRDYX, seeks to track the price and yield performance of the Gapstow Private Credit and Alternative Income Index before fees and expenses. The index is composed of registered closed-end funds, BDCs, and REITs listed on major U.S. exchanges.",
        previewBlurb:
            "Digital fund tracking liquid alternative credit exposure through CEFs, BDCs, and REITs.",
        fullScreenBlurb:
            "WisdomTree Private Credit and Alternative Income Digital Fund, or CRDYX, seeks to track the price and yield performance of the Gapstow Private Credit and Alternative Income Index before fees and expenses. The index is composed of registered closed-end funds, BDCs, and REITs listed on major U.S. exchanges.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target return disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Registered closed-end funds, BDCs and REITs listed on major U.S. exchanges.",
            },
            {
                title: "Liquidity",
                text: "The fund is positioned for daily liquidity, with subscriptions settling T+0 and redemptions settling T+2. The portfolio is exchange-listed CEFs, BDCs and REITs, but market stress could still affect execution values.",
            },
        ],
        documents: [
            {
                name: "CRDYX Product Materials",
                url: "https://www.wisdomtree.com/investments/digital-funds/asset-allocation/crdyx",
            },
        ],
    },
    {
        aliases: [
            "AA_FalconXUSDC",
            "AA_FalconX_usdc",
            "AA FalconX USDC",
            "FalconX Credit Vault",
            "falconx-credit-vault",
        ],
        blurb: "FalconX Credit Vault provides fixed-rate exposure to institutional credit lines managed by FalconX. Curated by M11 Credit, the vault includes structural protections such as an equity tranche, transaction-level collateral, and a real-time risk engine, with monthly interest distributions and monthly redemptions.",
        previewBlurb:
            "Credit vault offering fixed-rate exposure to FalconX institutional credit lines with monthly distributions.",
        fullScreenBlurb:
            "FalconX Credit Vault provides fixed-rate exposure to institutional credit lines managed by FalconX. Curated by M11 Credit, the vault includes structural protections such as an equity tranche, transaction-level collateral, and a real-time risk engine, with monthly interest distributions and monthly redemptions.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target return disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Institutional credit lines managed by FalconX, curated by M11 Credit with equity-tranche and transaction-level collateral protections.",
            },
            {
                title: "Liquidity",
                text: "The vault describes monthly interest distributions and monthly redemptions. In between cycles, liquidity is mainly onchain by using vault tokens as collateral in lending markets, so immediate exit depends on borrower demand, collateral rules and market depth.",
            },
        ],
        documents: [
            {
                name: "FalconX Credit Vault Product Materials",
                url: "https://app.pareto.credit/vault#0xc26a6fa2c37b38e549a4a1807543801db684f99c",
            },
        ],
    },
    {
        aliases: [
            "RCPC-KN",
            "RCPC KN",
            "VRPCW",
            "VRPCM",
            "VRPCQ",
            "VRPCS",
            "Helix Wave Private Credit",
            "Helix Wave High Yield Private Credit",
        ],
        blurb: "Helix Wave Private Credit is a work-in-progress receipt-token vault offering USDC exposure to senior tranches of diversified consumer-credit assets across Thailand, the Philippines, Indonesia, Pakistan and Mexico. The one-pager describes a 1 USDC minimum and expected APYs of 9%, 10%, 14% and 16% across seven-day, one-month, three-month and six-month receipt tokens. The return source is consumer-loan spread from an AI-powered lender group that reports US$22B of cumulative loans originated, with the portfolio described as thousands of small loans and 120% collateralization. Liquidity varies by token term and is not guaranteed; quarterly and semiannual tokens permit limited early withdrawal with a 2% fee and 10% holder-level cap. Missing information includes final vault terms, lender identity, audited portfolio performance, loan-level tape, FX hedging, loss history and legal recourse.",
        previewBlurb:
            "Receipt-token vault aiming for 9-16% APY through senior emerging-market consumer-credit tranches.",
        fullScreenBlurb:
            "Helix Wave Private Credit is a work-in-progress receipt-token vault offering USDC exposure to senior tranches of diversified consumer-credit assets across Thailand, the Philippines, Indonesia, Pakistan and Mexico. The one-pager describes a 1 USDC minimum and expected APYs of 9%, 10%, 14% and 16% across seven-day, one-month, three-month and six-month receipt tokens. The return source is consumer-loan spread from an AI-powered lender group that reports US$22B of cumulative loans originated, with the portfolio described as thousands of small loans and 120% collateralization. Liquidity varies by token term and is not guaranteed; quarterly and semiannual tokens permit limited early withdrawal with a 2% fee and 10% holder-level cap. Missing information includes final vault terms, lender identity, audited portfolio performance, loan-level tape, FX hedging, loss history and legal recourse.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "9-16% p.a. expected APY.",
            },
            {
                title: "Investment Universe",
                text: "Senior tranches of diversified consumer-credit assets across Thailand, the Philippines, Indonesia, Pakistan and Mexico.",
            },
            {
                title: "Liquidity",
                text: "Redemptions are available after the selected vault lock-up. Most withdrawals are expected within 24 hours, but can take up to seven days; quarterly and six-month tokens allow limited early withdrawal with a 2% fee and a 10% holder cap.",
            },
        ],
        documents: [
            {
                name: "RCPC KN One-Pager",
                url: "/data-rooms/rcpc-kn/RCPC-KN---One-Pager.pdf",
            },
        ],
    },
    {
        aliases: ["RCUSD", "rcUSD", "VRUSD", "Helix Wave Liquid"],
        blurb: "Helix Wave Liquid is a work-in-progress receipt-token vault for VRUSD, a USD asset-supported virtual asset backed by rcUSD and rcUSD+, which are described as supported by AAA-rated USD money-market funds and stablecoins. The one-pager describes daily deposit and withdrawal processing, a 1 USDC minimum, a 1% annual protocol fee currently waived, and a 4% base yield plus protocol incentives. The return source is USD money-market fund income and variable chain-level incentives, with Anchorage Digital named as custodian and SlowMist as smart-contract auditor. The product is not a guaranteed stablecoin and exposes users to MMF, stablecoin, custodian, smart-contract and protocol-incentive risks. Missing information includes final vault terms, reserve composition, current AUM, live net yield after fees, proof-of-reserve cadence and stress redemption history.",
        previewBlurb:
            "Receipt-token vault aiming for 4% plus incentives through AAA USD money-market funds and stablecoins.",
        fullScreenBlurb:
            "Helix Wave Liquid is a work-in-progress receipt-token vault for VRUSD, a USD asset-supported virtual asset backed by rcUSD and rcUSD+, which are described as supported by AAA-rated USD money-market funds and stablecoins. The one-pager describes daily deposit and withdrawal processing, a 1 USDC minimum, a 1% annual protocol fee currently waived, and a 4% base yield plus protocol incentives. The return source is USD money-market fund income and variable chain-level incentives, with Anchorage Digital named as custodian and SlowMist as smart-contract auditor. The product is not a guaranteed stablecoin and exposes users to MMF, stablecoin, custodian, smart-contract and protocol-incentive risks. Missing information includes final vault terms, reserve composition, current AUM, live net yield after fees, proof-of-reserve cadence and stress redemption history.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "4% base yield plus protocol incentives.",
            },
            {
                title: "Investment Universe",
                text: "USD asset-supported virtual asset backed by rcUSD and rcUSD+, supported by AAA-rated USD money-market funds and stablecoins.",
            },
            {
                title: "Liquidity",
                text: "Deposits and withdrawals are available daily, with requests before the cutoff generally processed within one business day. Timing can vary with eligibility checks, reserve availability and regional restrictions.",
            },
        ],
        documents: [
            {
                name: "RCUSD One-Pager",
                url: "/data-rooms/rcusd/RCUSD---One-Pager.pdf",
            },
        ],
    },
    {
        aliases: [
            "RCEQ-CC",
            "RCEQ CC",
            "VRCEQC",
            "Helix Wave Premier Growth",
            "Helix Wave Premier Growth Vault",
        ],
        blurb: "Helix Wave Premier Growth Vault is a work-in-progress receipt-token vault providing USDC on-chain access to a focused small and mid-cap public-equity strategy in East Asia. The one-pager describes VRCEQC, a 50,000 USDC minimum deposit and withdrawal, monthly deposits and withdrawals, expected March 2026 inception, and a 0.6% annual protocol fee. The return source is underlying public-equity strategy performance rather than contractual yield, with referenced historical returns of 247.7% since May 2023 and 99.7% in 2025 for a related class. The exposure is concentrated in high-growth sectors including healthcare and information technology, with equity volatility, sector concentration, liquidity and smart-contract risks. Missing information includes final vault terms, exact underlying vehicle, live portfolio weights, fee stack, redemption history and audited performance for the offered class.",
        previewBlurb:
            "Receipt-token vault aiming for equity upside through East Asia small/mid-cap public-growth stocks.",
        fullScreenBlurb:
            "Helix Wave Premier Growth Vault is a work-in-progress receipt-token vault providing USDC on-chain access to a focused small and mid-cap public-equity strategy in East Asia. The one-pager describes VRCEQC, a 50,000 USDC minimum deposit and withdrawal, monthly deposits and withdrawals, expected March 2026 inception, and a 0.6% annual protocol fee. The return source is underlying public-equity strategy performance rather than contractual yield, with referenced historical returns of 247.7% since May 2023 and 99.7% in 2025 for a related class. The exposure is concentrated in high-growth sectors including healthcare and information technology, with equity volatility, sector concentration, liquidity and smart-contract risks. Missing information includes final vault terms, exact underlying vehicle, live portfolio weights, fee stack, redemption history and audited performance for the offered class.",
        productSummarySections: [
            {
                title: "Target Returns",
                text: "No target return disclosed.",
            },
            {
                title: "Investment Universe",
                text: "Focused small and mid-cap public-equity strategy in East Asia, concentrated in high-growth sectors including healthcare and information technology.",
            },
            {
                title: "Liquidity",
                text: "Liquidity is monthly, with USDC deposits and withdrawals subject to a 50,000 USDC minimum. Settlement is expected under T+5, but depends on available proceeds and portfolio conditions.",
            },
        ],
        documents: [
            {
                name: "RCEQ-CC One-Pager",
                url: "/data-rooms/rceq-cc/RCEQ-CC---One-Pager.pdf",
            },
        ],
    },
];

const DEAL_ROOM_CONFIG_BY_SYMBOL: Record<string, DealRoomConfig> =
    DEAL_ROOM_CONFIGS.reduce<Record<string, DealRoomConfig>>(
        (configs, { aliases, ...config }) => {
            aliases.forEach((alias) => {
                const normalizedAlias = normalizeDealRoomKey(alias);
                if (normalizedAlias) {
                    configs[normalizedAlias] = config;
                }
            });

            return configs;
        },
        {},
    );

const DEAL_ROOM_CONFIG_BY_ID: Record<number, DealRoomConfig> = {};

const EXCLUDED_DEAL_ROOM_KEYS = new Set([
    "STAC",
    "MI4",
    "GACLO1",
    "GACLO 1",
    "GALAXY CLO 2025 1",
    "DEJAAA",
    "CMBMINT",
    "JAAAON",
    "CLOAON",
    "EURSPKCC",
    "EUSPKCC",
]);

function normalizeDealRoomKey(value: string | null | undefined): string | null {
    const normalized = value
        ?.trim()
        .toUpperCase()
        .replace(/&/g, " AND ")
        .replace(/[^A-Z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return normalized && normalized.length > 0 ? normalized : null;
}

export function getDealRoomConfig(
    asset: Pick<Asset, "id" | "ticker" | "slug" | "name">,
) {
    const tickerKey = normalizeDealRoomKey(asset.ticker);
    if (tickerKey && EXCLUDED_DEAL_ROOM_KEYS.has(tickerKey)) {
        return null;
    }

    const assetKeys = [asset.ticker, asset.slug, asset.name]
        .map(normalizeDealRoomKey)
        .filter((key): key is string => key !== null);

    for (const key of assetKeys) {
        if (DEAL_ROOM_CONFIG_BY_SYMBOL[key]) {
            return DEAL_ROOM_CONFIG_BY_SYMBOL[key];
        }
    }

    for (const [alias, config] of Object.entries(DEAL_ROOM_CONFIG_BY_SYMBOL)) {
        if (alias.length < 6) {
            continue;
        }

        if (
            assetKeys.some(
                (key) =>
                    key.length >= 6 &&
                    (key.includes(alias) || alias.includes(key)),
            )
        ) {
            return config;
        }
    }

    return DEAL_ROOM_CONFIG_BY_ID[asset.id] ?? null;
}
